import { watch } from 'vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import NProgress from 'nprogress'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceModeStore } from '@/stores/workspace-mode'
import { http, unwrap, setSessionLostHandler } from '@/api/client'
import { queryClient } from '@/api/query-client'
import { loadWorkspace } from '@/api/workspaces'
import { guardIntelligencePage } from './intelligence-guard'
import { i18n } from '@/i18n'
import type { ApiEnvelope, AuthenticatedUser } from '@/types/api'

/**
 * Access is declared in route meta and enforced by one guard, so a new page
 * cannot accidentally ship without an access rule.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, layout: 'auth' },
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/AuthCallbackView.vue'),
    meta: { public: true, layout: 'auth' },
  },
  {
    // Where a sign-in lands.
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
  },
  {
    path: '/admin/users',
    name: 'user-management',
    component: () => import('@/views/UserManagementView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/languages',
    name: 'language-management',
    component: () => import('@/views/LanguageManagementView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/admin/workspaces',
    name: 'workspace-management',
    component: () => import('@/views/AdminWorkspacesView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    /*
     * A workspace's Intelligence, under ElfieCare's own URL pattern
     * (DECISIONS.md #10), in its dedicated shell (P1 §4). Each page declares
     * what it needs (`capability`, lib/intelligence-pages.ts); a link someone
     * cannot use sends them Home with the reason, and the API refuses the data
     * whatever the router decides (S0 §14.2).
     *
     * The Overview (P2) heads the left menu: entering lands there for whoever
     * can use Intelligence in the workspace.
     */
    path: '/workspaces/:workspaceId/intelligence/overview',
    name: 'workspace-overview',
    component: () => import('@/views/WorkspaceOverviewView.vue'),
    meta: { layout: 'intelligence', capability: 'intelligence' },
  },
  {
    // The Opportunities feed, and one opportunity's Investigation view (P3).
    path: '/workspaces/:workspaceId/intelligence/opportunities',
    name: 'workspace-opportunities',
    component: () => import('@/views/WorkspaceOpportunitiesView.vue'),
    meta: { layout: 'intelligence', capability: 'intelligence' },
  },
  {
    path: '/workspaces/:workspaceId/intelligence/opportunities/:opportunityId',
    name: 'workspace-opportunity',
    component: () => import('@/views/WorkspaceOpportunityView.vue'),
    meta: { layout: 'intelligence', capability: 'intelligence' },
  },
  {
    // The action loop (P5, P6): objectives, saved reports and meeting dossiers, and My Attention.
    path: '/workspaces/:workspaceId/intelligence/objectives',
    name: 'workspace-objectives',
    component: () => import('@/views/WorkspaceObjectivesView.vue'),
    meta: { layout: 'intelligence', capability: 'intelligence' },
  },
  {
    path: '/workspaces/:workspaceId/intelligence/reports',
    name: 'workspace-reports',
    component: () => import('@/views/WorkspaceReportsView.vue'),
    meta: { layout: 'intelligence', capability: 'intelligence' },
  },
  {
    path: '/workspaces/:workspaceId/intelligence/reports/:reportId',
    name: 'workspace-report',
    component: () => import('@/views/WorkspaceReportView.vue'),
    meta: { layout: 'intelligence', capability: 'intelligence' },
  },
  {
    path: '/workspaces/:workspaceId/intelligence/attention',
    name: 'workspace-attention',
    component: () => import('@/views/WorkspaceAttentionView.vue'),
    meta: { layout: 'intelligence', capability: 'intelligence' },
  },
  {
    // Intelligence settings: one page, a tab each for what the person may see
    // (P1 §4.1, §6), reached from the gear in the header (DECISIONS.md #17).
    path: '/workspaces/:workspaceId/intelligence/settings',
    component: () => import('@/views/IntelligenceSettingsView.vue'),
    meta: { layout: 'intelligence' },
    children: [
      {
        path: '',
        name: 'workspace-members',
        component: () => import('@/views/WorkspaceMembersView.vue'),
        meta: { capability: 'seats' },
      },
      {
        // P1 §8: the clinic's profile, declared by its Owners and Admins (DECISIONS.md #35).
        path: 'clinic-profile',
        name: 'workspace-clinic-profile',
        component: () => import('@/views/WorkspaceClinicProfileView.vue'),
        meta: { capability: 'context' },
      },
      {
        // P1 §6.1: every reader's own, for whoever can use Intelligence here.
        path: 'my-function',
        name: 'workspace-my-function',
        component: () => import('@/views/WorkspaceMyFunctionView.vue'),
        meta: { capability: 'intelligence' },
      },
      {
        path: 'transparency',
        name: 'workspace-transparency',
        component: () => import('@/views/WorkspaceTransparencyView.vue'),
        meta: { capability: 'transparency' },
      },
    ],
  },
  {
    // The record's address before it became a settings tab (DECISIONS.md #17), so a saved link still opens it.
    path: '/workspaces/:workspaceId/intelligence/transparency',
    redirect: (to) => ({ name: 'workspace-transparency', params: { workspaceId: to.params.workspaceId } }),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: { name: 'home' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  // A new page opens at its top; a change of the address alone, such as Key figures' perspective, leaves the reader where they are.
  scrollBehavior: (to, from, saved) => saved ?? (to.path === from.path ? false : { top: 0 }),
})

NProgress.configure({ showSpinner: false })

/** Resolves the session once, then answers from the store. */
const ensureSession = async (): Promise<AuthenticatedUser | null> => {
  const auth = useAuthStore()
  if (auth.hasCheckedSession) return auth.user

  try {
    const { data } = await http.get<ApiEnvelope<AuthenticatedUser>>('/auth/me')
    auth.setUser(unwrap(data))
  } catch {
    auth.clear()
  }
  return auth.user
}

router.beforeEach(async (to) => {
  NProgress.start()
  const auth = useAuthStore()

  if (to.meta.public === true) return true

  const user = await ensureSession()
  if (!user) {
    // The path is preserved so re-auth returns them where they were.
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.requiresAdmin === true && !auth.isAdmin) {
    return { name: 'home' }
  }

  return guardIntelligencePage(to, (workspaceId) => loadWorkspace(queryClient, workspaceId))
})

/**
 * The tab reads the product name on its own, and "<product> – <section>" once
 * you are inside one. The product name leads so a row of tabs sorts by product.
 */
const sectionTitle = (path: string): string | null => {
  const t = i18n.global.t
  if (path.startsWith('/admin')) return t('nav.settings')
  if (path.endsWith('/transparency')) return t('nav.transparency')
  if (path.endsWith('/overview')) return t('nav.overview')
  if (path.includes('/intelligence/opportunities')) return t('nav.opportunities')
  if (path.endsWith('/intelligence/objectives')) return t('nav.objectives')
  if (path.includes('/intelligence/reports')) return t('nav.reports')
  if (path.endsWith('/intelligence/attention')) return t('nav.attention')
  if (path.startsWith('/workspaces/')) return t('nav.intelligenceSettings')
  if (path === '/login' || path.startsWith('/auth/')) return null
  return t('nav.home')
}

const updateTitle = (): void => {
  const appName = i18n.global.t('common.appName')
  const section = sectionTitle(router.currentRoute.value.path)
  document.title = section ? `${appName} – ${section}` : appName
}

router.afterEach((to, _from, failure) => {
  NProgress.done()
  updateTitle()
  // Where `Enter Intelligence` returns to (P1 §3.3).
  if (!failure && to.meta.layout === 'intelligence' && typeof to.params.workspaceId === 'string') {
    useWorkspaceModeStore().remember(to.params.workspaceId, to.fullPath)
  }
})

// The section name is translated, and the language can change after the
// navigation that set the title.
watch(i18n.global.locale, updateTitle)
router.onError(() => NProgress.done())

// A 401 anywhere in the app returns the user to login rather than showing an
// error page they cannot act on.
setSessionLostHandler(() => {
  const auth = useAuthStore()
  auth.clear()
  if (router.currentRoute.value.meta.public !== true) {
    void router.replace({
      name: 'login',
      query: { redirect: router.currentRoute.value.fullPath },
    })
  }
})
